import { resolveZonedWallTime, formatInstantAsWallTime } from '../events/zoned-date-time';
import type { AvailabilityWindowInput } from './service-one-to-one';
import { validateAvailabilityWindow } from './service-one-to-one';
import { slotsOverlap } from './service-booking';

export const DEFAULT_MIN_NOTICE_MINUTES = 120;
export const DEFAULT_MAX_ADVANCE_BOOKING_DAYS = 60;
export const MIN_NOTICE_MINUTES = 60;
export const MAX_NOTICE_MINUTES = 4320;
export const MIN_ADVANCE_BOOKING_DAYS = 7;
export const MAX_ADVANCE_BOOKING_DAYS = 365;
export const MAX_SCHEDULES_PER_CREATOR = 10;
export const MAX_BLOCKS_PER_SCHEDULE = 100;
export const DEFAULT_SCHEDULE_NAME = 'Default';

export type SchedulingProfileInput = {
  timezone: string;
  minNoticeMinutes: number;
  maxAdvanceBookingDays: number;
  reschedulePolicyText?: string | null;
};

export type BusyInterval = {
  startsAt: Date;
  endsAt: Date;
};

export type ExistingBookingSlot = {
  startsAt: Date;
  endsAt: Date;
  status: string;
  slotHoldExpiresAt: Date | null;
};

export type GenerateAvailabilitySlotsInput = {
  from: Date;
  to: Date;
  durationMinutes: number;
  bufferMinutes: number;
  minNoticeMinutes: number;
  maxBookingsPerDay: number;
  timezone: string;
  windows: AvailabilityWindowInput[];
  blockedDates: string[];
  busyIntervals: BusyInterval[];
  existing: ExistingBookingSlot[];
  now?: Date;
  maxBookableInstant?: Date;
};

export type EffectiveSlotQueryRange = {
  effectiveFrom: Date;
  effectiveTo: Date;
  beyondHorizon: boolean;
  horizonEnd: Date;
};

export function resolveEffectiveSlotQueryRange(input: {
  from: Date;
  to: Date;
  now?: Date;
  maxAdvanceBookingDays: number;
}): EffectiveSlotQueryRange {
  const now = input.now ?? new Date();
  const horizonEnd = new Date(now.getTime() + input.maxAdvanceBookingDays * 24 * 3600_000);
  if (input.from.getTime() > horizonEnd.getTime()) {
    return {
      effectiveFrom: input.from,
      effectiveTo: input.from,
      beyondHorizon: true,
      horizonEnd,
    };
  }
  return {
    effectiveFrom: input.from,
    effectiveTo: new Date(Math.min(input.to.getTime(), horizonEnd.getTime())),
    beyondHorizon: false,
    horizonEnd,
  };
}

const WEEKDAY_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function isValidTimeZone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date());
    return Boolean(timezone.trim());
  } catch {
    return false;
  }
}

function parseDateKey(dateKey: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
}

function addDaysToDateKey(dateKey: string, days: number): string {
  const parts = parseDateKey(dateKey);
  if (!parts) return dateKey;
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return date.toISOString().slice(0, 10);
}

function dateKeyInTimeZone(instant: Date, timeZone: string): string {
  return formatInstantAsWallTime(instant, timeZone).slice(0, 10);
}

function dayOfWeekForDateKey(dateKey: string, timeZone: string): number | null {
  const resolved = resolveZonedWallTime(`${dateKey}T12:00:00`, timeZone);
  if (!resolved.ok) return null;
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
  }).format(resolved.instant);
  return WEEKDAY_TO_INDEX[weekday] ?? null;
}

function minuteToClock(minute: number): string {
  const hours = Math.floor(minute / 60);
  const mins = minute % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
}

export function validateSchedulingProfile(input: SchedulingProfileInput): string[] {
  const errors: string[] = [];
  if (!isValidTimeZone(input.timezone)) errors.push('Timezone is invalid');
  if (
    !Number.isInteger(input.minNoticeMinutes) ||
    input.minNoticeMinutes < MIN_NOTICE_MINUTES ||
    input.minNoticeMinutes > MAX_NOTICE_MINUTES
  ) {
    errors.push(`Notice period must be between ${MIN_NOTICE_MINUTES} and ${MAX_NOTICE_MINUTES} minutes`);
  }
  if (
    !Number.isInteger(input.maxAdvanceBookingDays) ||
    input.maxAdvanceBookingDays < MIN_ADVANCE_BOOKING_DAYS ||
    input.maxAdvanceBookingDays > MAX_ADVANCE_BOOKING_DAYS
  ) {
    errors.push(
      `Booking period must be between ${MIN_ADVANCE_BOOKING_DAYS} and ${MAX_ADVANCE_BOOKING_DAYS} days`,
    );
  }
  return errors;
}

export function validateScheduleName(name: string): string[] {
  const trimmed = name.trim();
  if (!trimmed) return ['Schedule name is required'];
  if (trimmed.length > 80) return ['Schedule name must be 80 characters or fewer'];
  return [];
}

export function validateAvailabilityWindows(windows: AvailabilityWindowInput[]): string[] {
  const errors: string[] = [];
  windows.forEach((window, index) => {
    validateAvailabilityWindow(window).forEach((message) => {
      errors.push(`Window ${index + 1}: ${message}`);
    });
  });
  return errors;
}

export function validateBlockedDate(dateKey: string): string[] {
  if (!parseDateKey(dateKey)) return ['Blocked date must use YYYY-MM-DD format'];
  return [];
}

export function generateAvailabilitySlots(input: GenerateAvailabilitySlotsInput) {
  const now = input.now ?? new Date();
  const minStart = now.getTime() + input.minNoticeMinutes * 60_000;
  const slots: Array<{ startsAt: string; endsAt: string }> = [];
  const dayCounts = new Map<string, number>();
  const blocked = new Set(input.blockedDates);

  const startKey = dateKeyInTimeZone(input.from, input.timezone);
  const endKey = dateKeyInTimeZone(input.to, input.timezone);

  for (let dateKey = startKey; dateKey <= endKey; dateKey = addDaysToDateKey(dateKey, 1)) {
    if (blocked.has(dateKey)) continue;

    const dayOfWeek = dayOfWeekForDateKey(dateKey, input.timezone);
    if (dayOfWeek == null) continue;

    for (const window of input.windows) {
      if (window.dayOfWeek !== dayOfWeek) continue;

      let cursor = window.startMinute;
      while (cursor + input.durationMinutes <= window.endMinute) {
        const startWall = `${dateKey}T${minuteToClock(cursor)}`;
        const resolvedStart = resolveZonedWallTime(startWall, input.timezone);
        if (!resolvedStart.ok) {
          cursor += input.durationMinutes + input.bufferMinutes;
          continue;
        }

        const startsAt = resolvedStart.instant;
        const endsAt = new Date(startsAt.getTime() + input.durationMinutes * 60_000);

        if (startsAt.getTime() < input.from.getTime()) {
          cursor += input.durationMinutes + input.bufferMinutes;
          continue;
        }
        if (startsAt.getTime() > input.to.getTime()) break;
        if (input.maxBookableInstant && startsAt.getTime() > input.maxBookableInstant.getTime()) {
          cursor += input.durationMinutes + input.bufferMinutes;
          continue;
        }
        if (startsAt.getTime() < minStart) {
          cursor += input.durationMinutes + input.bufferMinutes;
          continue;
        }

        const count = dayCounts.get(dateKey) ?? 0;
        if (count >= input.maxBookingsPerDay) break;

        const blockedByBooking = input.existing.some((booking) => {
          if (
            booking.status === 'slot_held' &&
            booking.slotHoldExpiresAt &&
            booking.slotHoldExpiresAt.getTime() < now.getTime()
          ) {
            return false;
          }
          if (booking.status === 'cancelled' || booking.status === 'no_show') return false;
          return slotsOverlap(
            startsAt,
            endsAt,
            booking.startsAt,
            booking.endsAt,
            input.bufferMinutes,
          );
        });

        const blockedByBusy = input.busyIntervals.some((interval) =>
          slotsOverlap(startsAt, endsAt, interval.startsAt, interval.endsAt, input.bufferMinutes),
        );

        if (!blockedByBooking && !blockedByBusy) {
          slots.push({
            startsAt: startsAt.toISOString(),
            endsAt: endsAt.toISOString(),
          });
          dayCounts.set(dateKey, count + 1);
        }

        cursor += input.durationMinutes + input.bufferMinutes;
      }
    }
  }

  return slots;
}

export function defaultBookingRange(now: Date, maxAdvanceBookingDays: number, timeZone: string) {
  const from = now;
  const toInstant = new Date(now.getTime() + maxAdvanceBookingDays * 24 * 3600_000);
  return {
    from,
    to: toInstant,
    fromDate: dateKeyInTimeZone(from, timeZone),
    toDate: dateKeyInTimeZone(toInstant, timeZone),
    maxAdvanceBookingDays,
  };
}
